import React, {useState, useRef, useEffect} from "react";
import {Controller, useFormState} from "react-hook-form";
import {Calendar} from "react-date-range";
import {Popper} from "@mui/material";
import moment from "moment";
import {DateInput} from "rsuite";
import {enGB} from "date-fns/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {Svg} from "assets";
import {getNestedError, newDateWithoutUtcCalendar} from "utils";

export const DateCalendarForm = ({
                                     label,
                                     control,
                                     name,
                                     rules,
                                     max,
                                     min,
                                     disabled,
                                     closeModal = true,
                                     dontShowError
                                 }) => {
    const [isFocused, setFocused] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const inputRef = useRef(null);
    const prevHasError = useRef(false);
    const { errors } = useFormState({ control });
    const error = getNestedError(errors, name);
    const hasError = !!error?.type;
    const open = Boolean(anchorEl);
    const id = open ? "simple-popper" : undefined;

    useEffect(() => {
        if (hasError && !prevHasError.current && inputRef.current) {
            inputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        prevHasError.current = hasError;
    }, [hasError]);

    const customLocale = {
        ...enGB,
        options: {
            ...enGB.options,
            weekStartsOn: 0,
        },
    };

    const handleClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };


    const isValidDate = (dateString) => {
        const date = new Date(dateString);
        return !isNaN(date.getTime());
    };

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({field, formState: {errors}}) => {
                return (
                    <>
                        <div className="date-calendar-wrapper">
                            {label && (
                                <p className="date-input-title">
                                    {`${label}${rules?.required ? "*" : ""}`}
                                </p>
                            )}

                            <button
                                ref={(el) => {
                                    field.ref(el);
                                    inputRef.current = el;
                                }}
                                disabled={disabled}
                                onClick={handleClick}
                                className={`date-calendar-main-button ${
                                    isFocused ? "date-calendar-main-button-focused" : hasError ? "date-calendar-main-button-error" : ""
                                }`}
                                aria-describedby={id}
                                type={"button"}
                            >
                                <div
                                    className="date-range-inputs"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }}
                                >
                                    <DateInput
                                        min={min}
                                        max={max}
                                        disabled={disabled}
                                        placeholder={"mm/dd/yyyy"}
                                        onFocus={() => setFocused(true)}
                                        onBlur={() => setFocused(false)}
                                        format="MM/dd/yyyy"
                                        value={field?.value ? moment(field?.value).toDate() : null}
                                        onChange={(date) => {
                                            if (date && date > new Date(2045, 11, 31)) return;

                                            const isValid = isValidDate(date);
                                            const minDate = min ? new Date(min) : null;
                                            const maxDate = max ? new Date(max) : null;
                                            const isWithinRange =
                                                (!minDate || date >= minDate) &&
                                                (!maxDate || date <= maxDate);

                                            if (isValid && isWithinRange) {
                                                const formattedDate = moment(date).format("YYYY-MM-DD");
                                                field.onChange(formattedDate);
                                            }
                                        }}
                                    />
                                </div>
                                <div className="calendar-icon-clear-button">
                                    {field?.value && (
                                        <button
                                            className="remove-calendar-day"
                                            type={"button"}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                field.onChange(null);
                                            }}
                                        >
                                            <Svg.CloseSvg/>
                                        </button>
                                    )}

                                    <Svg.CalendarSvg style={{opacity: disabled ? 0.7 : 1}}/>
                                </div>
                            </button>
                            {!dontShowError &&
                                <p className="error-message">{error?.message}</p>
                            }
                            <Popper
                                id={id}
                                open={open}
                                anchorEl={anchorEl}
                                className={"modal-custom-date-selector-box"}
                                placement="bottom-start"
                                style={{position: "absolute", zIndex: 1400}}
                            >
                                <Calendar
                                    date={field?.value ? moment(field?.value).toDate() : null}
                                    onChange={(date) => {
                                        const formattedDate = moment(date).format("YYYY-MM-DD");
                                        field.onChange(formattedDate);
                                        if (closeModal) setAnchorEl(null)
                                    }}
                                    locale={customLocale}
                                    maxDate={max ? newDateWithoutUtcCalendar(max) : undefined}
                                    minDate={min ? newDateWithoutUtcCalendar(min) : undefined}
                                    disabledDay={(date) => false}
                                />
                            </Popper>
                            {open && (
                                <div className="beck-drop" onClick={() => setAnchorEl(null)}/>
                            )}
                        </div>
                    </>
                );
            }}
        />
    );
};
